import {
  ArrowUpIcon,
  CloseIcon,
  EditIcon,
  HamburgerIcon,
} from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SIDE_PANEL_WIDTH } from '../../constants/panelWidths';
import SpeechInput, {
  type SpeechInputHandle,
} from '../../filters/components/filterForms/talent-discovery/ai-search/SpeechInput';
import { isDefaultUntitledName } from '../../utils/generate-unique-title';
import { QuestionsPanel } from '../AIBuilder/Questions/QuestionsPanel';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { ConversationHistoryPanel } from './ConversationHistoryPanel';
import { ConversationView } from './ConversationView/ConversationView';
import { type AIEntityType, useAIAssistant } from './useAIAssistant';
import {
  type ReportAIEntityType,
  useReportAIAssistant,
} from './useReportAIAssistant';

export type AIAssistantMode = 'dashboard' | 'report';

interface AIAssistantDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: AIAssistantMode;
  /** For dashboard: entity type (company/geography/role). For report: primary parameter type. */
  entityType: AIEntityType | ReportAIEntityType;
}

// Available models on OpenRouter
interface ModelOption {
  value: string;
  label: string;
}

const AVAILABLE_MODELS: ModelOption[] = [
  {
    value: 'anthropic/claude-sonnet-4',
    label: 'Claude Sonnet 4 - Best Accuracy',
  },
  {
    value: 'openai/gpt-4o',
    label: 'GPT-4o - High Accuracy',
  },
  {
    value: 'deepseek/deepseek-chat',
    label: 'DeepSeek V3 - Best Value',
  },
  {
    value: 'deepseek/deepseek-r1',
    label: 'DeepSeek R1 - Deep Reasoning',
  },
  {
    value: 'google/gemini-2.0-flash-thinking-exp:free',
    label: 'Gemini 2.0 Flash Thinking - Free',
  },
  {
    value: 'openai/gpt-4o-mini',
    label: 'GPT-4o Mini - Fastest',
  },
  {
    value: 'anthropic/claude-3.5-sonnet',
    label: 'Claude 3.5 Sonnet - Balanced',
  },
  {
    value: 'meta-llama/llama-3.3-70b-instruct',
    label: 'Llama 3.3 70B - Open Source',
  },
];

export function AIAssistantDrawer({
  isOpen,
  onClose,
  mode,
  entityType,
}: AIAssistantDrawerProps) {
  const [prompt, setPrompt] = useState('');
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [selectedModel, _setSelectedModel] = useState<string>(
    AVAILABLE_MODELS?.[0]?.value ?? ''
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const speechInputRef = useRef<SpeechInputHandle>(null);

  const onCloseAndStopRecording = useCallback(() => {
    speechInputRef.current?.stopRecording();
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      speechInputRef.current?.stopRecording();
    }
  }, [isOpen]);

  const handleStartListening = useCallback(() => {
    // Append-only: keep existing prompt when starting voice input
  }, []);

  const { config: tabContent, dispatch } = useTabContentConfig();
  const { config: pageConfig, dispatch: pageDispatch } = usePageConfig();

  const pageNameRef = useRef(pageConfig.name);
  useEffect(() => {
    pageNameRef.current = pageConfig.name;
  }, [pageConfig.name]);

  const handleAITitleGenerated = useCallback(
    (title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      // Only adopt the AI-generated title when the page is still using its
      // auto-generated default ("Untitled" / "Untitled 2" / ...). This keeps
      // user-renamed pages from being silently overwritten.
      if (!isDefaultUntitledName(pageNameRef.current)) return;
      pageDispatch({ type: 'UPDATE_PAGE_TITLE', payload: trimmed });
    },
    [pageDispatch]
  );

  const dashboardAssistant = useAIAssistant({
    entityType: entityType as AIEntityType,
    getCurrentTabContent: () => tabContent,
    dispatch,
    onTitleGenerated: handleAITitleGenerated,
  });

  const reportAssistant = useReportAIAssistant({
    primaryParameterType: entityType as ReportAIEntityType,
    getCurrentTabContent: () => tabContent,
    dispatch,
    onTitleGenerated: handleAITitleGenerated,
  });

  const {
    thinking,
    status: _status,
    intent,
    isGenerating,
    isLoadingConversation,
    error,
    generate,
    update,
    reset,
    promptHistory,
    responseSummaries,
    conversationTitle,
    questions,
    continueWithAnswers,
    skipQuestions,
    conversations,
    conversationId,
    fetchConversations,
    loadConversation,
    configSnapshots,
    restoreConfig,
  } = mode === 'report' ? reportAssistant : dashboardAssistant;

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setPrompt(value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 200;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  };

  const handleGenerate = () => {
    if (prompt.trim()) {
      const currentPrompt = prompt;
      setPrompt('');
      // Reset textarea height after clearing
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // If intent exists, use update; otherwise generate new
      if (intent) {
        update(currentPrompt, selectedModel);
      } else {
        generate(currentPrompt, selectedModel);
      }
    }
  };

  const handleNewChat = () => {
    reset();
    setPrompt('');
    // Reset textarea height after clearing
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  // Sync textarea height when prompt changes (e.g. from speech transcript)
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 200;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [prompt]);

  // Fetch conversations when drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen, fetchConversations]);

  // Escape: close history panel if open, otherwise close the drawer
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (historyPanelOpen) {
        setHistoryPanelOpen(false);
      } else {
        onCloseAndStopRecording();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, historyPanelOpen, onCloseAndStopRecording]);

  return (
    <Box
      position="fixed"
      top={0}
      right={0}
      height="100vh"
      width={SIDE_PANEL_WIDTH}
      bg="white"
      boxShadow="lg"
      transform={isOpen ? 'translateX(0)' : `translateX(${SIDE_PANEL_WIDTH})`}
      transition="transform 0.3s ease-in-out"
      zIndex={100}
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* Full gray overlay when history panel is open; 20% strip in panel is transparent so this single shade shows through */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.400"
        opacity={historyPanelOpen ? 1 : 0}
        transition="opacity 0.2s"
        zIndex={5}
        pointerEvents="none"
      />

      {/* Chat content - shifts right when history panel is open */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        minH={0}
        overflow="hidden"
        transform={historyPanelOpen ? 'translateX(80%)' : 'translateX(0)'}
        transition="transform 0.3s ease-in-out"
      >
        {/* Header - compact padding to match page header height */}
        <Flex
          align="center"
          justify="space-between"
          py={1}
          px={3}
          borderBottom="1px solid"
          borderColor="gray.200"
          flexShrink={0}
        >
          <IconButton
            aria-label="Open conversation history"
            icon={<HamburgerIcon />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            onClick={() => setHistoryPanelOpen(true)}
          />
          <Heading
            size="sm"
            color="gray.800"
            fontWeight="600"
            noOfLines={1}
            flex={1}
            textAlign="left"
            px={2}
          >
            {conversationTitle || (mode === 'report' ? 'AI Report' : 'AI Chat')}
          </Heading>
          <Flex align="center" gap={0}>
            <IconButton
              aria-label="New chat"
              icon={<EditIcon boxSize={4} />}
              size="md"
              variant="ghost"
              colorScheme="gray"
              onClick={handleNewChat}
              isDisabled={isGenerating}
            />
            <IconButton
              aria-label="Close AI Assistant"
              icon={<CloseIcon boxSize={3} />}
              size="md"
              variant="ghost"
              colorScheme="gray"
              onClick={onCloseAndStopRecording}
            />
          </Flex>
        </Flex>

        {/* Scrollable Content */}
        <Flex direction="column" flex={1} overflow="hidden" p={4}>
          {/* Chat area - scrollable */}
          <Box
            flex={1}
            minH={0}
            mb={4}
            display="flex"
            flexDirection="column"
            position="relative"
          >
            {/* Conversation View - handles its own scrolling */}
            <Box flex={1} minH={0} overflow="auto">
              <ConversationView
                promptHistory={promptHistory}
                responseSummaries={responseSummaries}
                thinking={thinking}
                isGenerating={isGenerating}
                configSnapshots={configSnapshots}
                restoreConfig={restoreConfig}
              />
            </Box>
            {isLoadingConversation && (
              <Flex
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                bg="whiteAlpha.800"
                flexDirection="column"
                gap={3}
              >
                <Spinner size="lg" color="green.500" />
                <Text fontSize="sm" color="gray.600">
                  Loading conversation...
                </Text>
              </Flex>
            )}

            {/* Error - at bottom of scroll area */}
            {error && (
              <Alert
                status="error"
                borderRadius="md"
                fontSize="sm"
                mt={4}
                flexShrink={0}
              >
                <AlertIcon />
                <Box>
                  <Text fontWeight="medium">Generation Failed</Text>
                  <Text fontSize="xs">{error}</Text>
                </Box>
              </Alert>
            )}
          </Box>

          {/* Questions Panel - fixed at bottom, above input */}
          {questions && questions.length > 0 && (
            <Box mb={4} flexShrink={0}>
              <QuestionsPanel
                questions={questions}
                onComplete={continueWithAnswers}
                onSkip={skipQuestions}
              />
            </Box>
          )}

          {/* Fixed bottom section - Prompt Input */}
          <Box flexShrink={0} pt={4} pb={4}>
            <Box
              bg="gray.50"
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.200"
              position="relative"
              display="flex"
              flexDirection="column"
            >
              {/* Textarea that auto-resizes */}
              <Textarea
                ref={textareaRef}
                placeholder={
                  mode === 'report'
                    ? 'Describe the report you want...'
                    : 'Ask AI...'
                }
                value={prompt}
                onChange={handlePromptChange}
                fontSize="sm"
                border="none"
                bg="transparent"
                resize="none"
                overflowY="auto"
                p={3}
                pb={1}
                rows={1}
                maxH="200px"
                style={{
                  height: 'auto',
                  minHeight: '40px',
                }}
                _focus={{
                  outline: 'none',
                  boxShadow: 'none',
                }}
                isDisabled={isGenerating}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleGenerate();
                  }
                  if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (textareaRef.current) {
                      textareaRef.current.select();
                    }
                  }
                }}
              />
              <Flex align="center" justify="flex-end" gap={1} p={1}>
                <SpeechInput
                  ref={speechInputRef}
                  onTranscriptComplete={(transcript) => {
                    setPrompt((prev) =>
                      prev ? `${prev} ${transcript}` : transcript
                    );
                  }}
                  onEnter={handleGenerate}
                  onStartListening={handleStartListening}
                  textAreaRef={textareaRef}
                />
                <IconButton
                  aria-label="Send"
                  icon={<ArrowUpIcon />}
                  onClick={handleGenerate}
                  isLoading={isGenerating}
                  isDisabled={!prompt.trim() || isGenerating}
                  size="sm"
                  borderRadius="full"
                  bg="green.400"
                  color="white"
                  _hover={{
                    bg: 'green.500',
                  }}
                  _active={{
                    bg: 'green.600',
                  }}
                  transition="all 0.2s"
                />
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Box>

      <ConversationHistoryPanel
        isOpen={historyPanelOpen}
        onClose={() => setHistoryPanelOpen(false)}
        conversations={conversations}
        currentConversationId={conversationId ?? undefined}
        onSelectConversation={(id) => {
          loadConversation(id);
          setHistoryPanelOpen(false);
        }}
        onNewChat={handleNewChat}
      />
    </Box>
  );
}
